import jsPDF from 'jspdf';
import type { FoodObservation } from './FoodTestVisuals';

export interface FoodReportDetails {
  studentName: string;
  observations: FoodObservation[];
  conclusion: string;
  unknownFoodName?: string;
}

/** Creates and downloads a printable report from the current food-test notebook. */
export function generateFoodTestReport({ studentName, observations, conclusion, unknownFoodName }: FoodReportDetails): Blob {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 18;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 18) {
      pdf.addPage();
      y = 18;
    }
  };
  const write = (text: string, size = 10, gap = 6) => {
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(text, pageWidth - 28) as string[];
    ensureSpace(lines.length * gap + 2);
    pdf.text(lines, 14, y);
    y += lines.length * gap + 2;
  };

  pdf.setTextColor(24, 67, 58);
  write('Virtual Food Tests Laboratory', 18, 8);
  pdf.setTextColor(35, 45, 42);
  write(`Student: ${studentName || 'Student'}    Date: ${new Date().toLocaleDateString()}`);
  write('Aim: Test food samples for reducing and non-reducing sugars, starch, protein, lipids, and vitamin C.', 10, 5);
  write('Apparatus: test tubes, rack, droppers, water bath, thermometer, holder, white tile, and food-test reagents.', 10, 5);
  if (unknownFoodName) write(`Unknown sample identification: ${unknownFoodName}`, 10, 5);

  y += 3;
  observations.forEach((item, index) => {
    ensureSpace(30);
    pdf.setFont('helvetica', 'bold');
    write(`${index + 1}. ${item.foodName} | ${item.test.replaceAll('-', ' ')} | ${item.category}`, 10, 5);
    pdf.setFont('helvetica', 'normal');
    write(`Observation: ${item.observation} (colour: ${item.colourName})`, 9, 5);
    write(`Inference: ${item.inference || 'Not recorded'}`, 9, 5);
    write(`Recorded: ${new Date(item.timestamp).toLocaleString()}`, 8, 4);
  });

  y += 3;
  pdf.setFont('helvetica', 'bold');
  write('Conclusion and discussion', 12, 6);
  pdf.setFont('helvetica', 'normal');
  write(conclusion || 'No conclusion recorded.', 10, 5);

  const blob = pdf.output('blob');
  pdf.save('virtual-food-tests-report.pdf');
  return blob;
}